import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, Shield, Lock } from 'lucide-react';

const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return 'bg-red-100 text-red-700';
    case 'high': return 'bg-orange-100 text-orange-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    case 'low': return 'bg-blue-100 text-blue-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const IoTVulnerabilitiesTab = ({ vulnerabilities, scanResult }) => (
  <div className="space-y-4">
    <div className="bg-white rounded-xl border shadow-sm p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Known IoT Vulnerabilities Database</h3>
      <div className="space-y-3">
        {vulnerabilities.map((vuln) => (
          <div key={vuln.cve_id} className={`p-4 rounded-lg border ${vuln.severity === 'critical' ? 'border-red-200 bg-red-50' : vuln.severity === 'high' ? 'border-orange-200 bg-orange-50' : 'border-yellow-200 bg-yellow-50'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`h-5 w-5 ${vuln.severity === 'critical' ? 'text-red-600' : vuln.severity === 'high' ? 'text-orange-600' : 'text-yellow-600'}`} />
                <span className="font-mono text-sm font-medium">{vuln.cve_id}</span>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getSeverityColor(vuln.severity)}`}>{vuln.severity}</span>
            </div>
            <h4 className="font-medium text-gray-900">{vuln.name}</h4>
            <p className="text-sm text-gray-600 mt-1">{vuln.description}</p>
            <div className="mt-2">
              <span className="text-xs text-gray-500">Affected Firmware: </span>
              <span className="text-xs text-gray-700">{vuln.affected_firmware?.join(', ')}</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {scanResult && (
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Latest Firmware Scan Result</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between"><span className="text-gray-600">Firmware Version</span><span className="font-mono">{scanResult.firmware_version}</span></div>
          <div className="flex items-center justify-between"><span className="text-gray-600">Overall Risk</span><span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getSeverityColor(scanResult.overall_risk)}`}>{scanResult.overall_risk}</span></div>
          <div className="flex items-center justify-between"><span className="text-gray-600">Vulnerabilities Found</span><span className="font-bold text-red-600">{scanResult.vulnerabilities_found}</span></div>
          {scanResult.vulnerabilities?.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="font-medium text-gray-900">Detected Vulnerabilities:</h4>
              {scanResult.vulnerabilities.map((v) => (
                <div key={v.cve_id} className="p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{v.cve_id}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(v.severity)}`}>{v.severity}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1">{v.description}</p>
                  <p className="text-xs text-blue-600 mt-1">{v.remediation}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

export const IoTAccessControlTab = () => (
  <div className="bg-white rounded-xl border shadow-sm p-6">
    <h3 className="font-semibold text-gray-900 mb-4">Access Control Management</h3>
    <p className="text-gray-600 mb-6">Define and manage access control rules for your IoT endpoints</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="p-4 bg-green-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2"><CheckCircle className="h-5 w-5 text-green-600" /><h4 className="font-medium text-green-800">Default Allow Rules</h4></div>
        <ul className="text-sm text-green-700 space-y-1">
          <li>MQTT to cloud broker (port 8883)</li>
          <li>HTTPS to API endpoints (port 443)</li>
          <li>NTP synchronization (port 123)</li>
        </ul>
      </div>
      <div className="p-4 bg-red-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2"><XCircle className="h-5 w-5 text-red-600" /><h4 className="font-medium text-red-800">Default Deny Rules</h4></div>
        <ul className="text-sm text-red-700 space-y-1">
          <li>Telnet access (port 23)</li>
          <li>FTP access (port 21)</li>
          <li>Unknown outbound connections</li>
        </ul>
      </div>
    </div>
    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
      <h4 className="font-medium text-blue-800 mb-2">Best Practices</h4>
      <ul className="text-sm text-blue-700 space-y-1">
        <li>Implement network segmentation for IoT devices</li>
        <li>Use encrypted protocols (TLS/SSL) for all communications</li>
        <li>Regularly audit and update access control rules</li>
        <li>Monitor for unauthorized access attempts in real-time</li>
      </ul>
    </div>
  </div>
);
